import Image from '@/components/Image';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';

// #region - Components

const CapabilityBlock = styled.div<{ accentColor: string }>`
  border-left: ${remCalc(4)} solid ${(props) => props.accentColor};
  padding-left: ${remCalc(18)};

  span {
    display: block;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(12, 17)}
    letter-spacing: 0.03em;
    text-transform: uppercase;
    margin-bottom: ${remCalc(10)};
  }

  p {
    ${customTextSize(16, 22)}

    strong {
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }
  }

  h3 {
    ${customTextSize(19, 22)}
    margin-bottom: ${remCalc(10)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    span {
      ${customTextSize(15, 17)}
    }

    h3 {
      ${customTextSize(24, 30)}
    }

    p {
      ${customTextSize(18, 25)}
    }
  }
`;

const CapabilityBlocksWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  row-gap: ${remCalc(60)};
  column-gap: ${remCalc(20)};
  margin-bottom: ${remCalc(80)};

  ${CapabilityBlock} {
    position: relative;
    grid-column: 1 / span 4;
  }

  ${CapabilityBlock}:nth-child(2n) {
    grid-column: 2 / span 4;
    left: ${remCalc(-20)};
  }

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(12, 1fr);
    row-gap: 0;
    margin-bottom: ${remCalc(100)};

    ${CapabilityBlock} {
      width: calc(100% + ${remCalc(20)});
    }

    ${CapabilityBlock}:nth-child(2n) {
      left: 0;
    }

    ${CapabilityBlock}:nth-child(1) {
      grid-column: 1 / span 5;
      grid-row: 1;
    }

    ${CapabilityBlock}:nth-child(2) {
      grid-column: 7 / span 5;
      grid-row: 2;
      margin-top: ${remCalc(-110)};
    }

    ${CapabilityBlock}:nth-child(3) {
      grid-column: 4 / span 5;
      grid-row: 3;
      margin-top: 0;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: none;
    grid-auto-columns: minmax(0, ${100 / 3}%);
    column-gap: ${remCalc(22)};
    align-items: flex-start;
    justify-content: center;
    margin-bottom: ${remCalc(160)};

    ${CapabilityBlock} {
      width: auto;
      flex: 1;
      margin: 0;
    }

    ${CapabilityBlock}:nth-child(1) {
      margin-left: 0;
      grid-column: 1 / span 1;
      grid-row: 1;
    }

    ${CapabilityBlock}:nth-child(2) {
      margin-top: ${remCalc(141)};
      grid-column: 2 / span 1;
      grid-row: 1;
    }

    ${CapabilityBlock}:nth-child(3) {
      margin-top: ${remCalc(141 * 2)};
      grid-column: 3 / span 1;
      grid-row: 1;
    }
  }
`;

const CapabilityBlockIcon = styled.div`
  margin-bottom: ${remCalc(10)};
`;

// #endregion - Components

const ACCENT_COLORS = [COLORS.RED.BASE, COLORS.YELLOW.LOWLIGHT, COLORS.GREEN.BASE];

interface CapabilityBlocksProps extends PropsWithClassName {
  blocks: {
    icon?: {
      src: string;
      width: number;
      height: number;
    };
    supertitle?: string;
    title: string;
    text: ReactNode;
    children?: ReactNode;
  }[];
}

const CapabilityBlocks: FC<CapabilityBlocksProps> = ({ blocks, className }) => {

  return (
    <CapabilityBlocksWrapper className={className}>
      {blocks.map((block, i) => (
        <CapabilityBlock key={`capability-block-${block.title?.replace(/\s/g, '-') ?? i}`} accentColor={ACCENT_COLORS[i]}>
          {block.icon && (
            <CapabilityBlockIcon>
              <Image {...block.icon} alt="" />
            </CapabilityBlockIcon>
          )}

          {block.supertitle && <span>{block.supertitle}</span>}

          <h3>{block.title}</h3>
          <div>{block.text}</div>

          {block.children}
        </CapabilityBlock>
      ))}
    </CapabilityBlocksWrapper>
  );
};

export default CapabilityBlocks;
